import React, { useRef, useEffect, useState, useMemo } from 'react';
import { EnhancedTableProps, SortConfig, PaginationConfig } from './types';
import { getColumnWidth, getCellContent, getCellClassName, getSortIndicator, getSortAttribute, getCellTitleProperty } from './utils';
import './EnhancedTableBase.css';

/**
 * Pagination controls component
 */
export function PaginationControls({
  pagination,
  className = ''
}: {
  pagination: PaginationConfig;
  className?: string;
}) {
  const { currentPage, pageSize, totalCount, onPageChange, onPageSizeChange, pageSizeOptions = [25, 50, 100] } = pagination;

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / pageSize);
  const startRow = (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalCount);

  // Generate page numbers with ellipsis for large page ranges
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page + ellipsis if needed
    if (startPage > 1) {
      pageNumbers.push(
        <button
          key="1"
          className="pagination-button"
          onClick={() => onPageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pageNumbers.push(<span key="ellipsis1" className="px-1">...</span>);
      }
    }

    // Visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          className={`pagination-button ${currentPage === i ? 'active' : ''}`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Ellipsis + last page if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(<span key="ellipsis2" className="px-1">...</span>);
      }
      pageNumbers.push(
        <button
          key={totalPages}
          className="pagination-button"
          onClick={() => onPageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pageNumbers;
  };

  return (
    <div className={`pagination-controls ${className}`}>
      {/* Information about displayed entries */}
      <div className="text-sm text-gray-600">
        {totalCount === 0 ? (
          'No entries to display'
        ) : (
          `Showing ${startRow} to ${endRow} of ${totalCount} entries`
        )}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center space-x-2">
        {/* Page size selector */}
        <select
          className="border rounded p-1 text-sm"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>{size} per page</option>
          ))}
        </select>

        {/* Previous page button */}
        <button
          className={`pagination-button ${currentPage === 1 ? 'disabled' : ''}`}
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>

        {/* Page number buttons */}
        <div className="flex space-x-1">
          {renderPageNumbers()}
        </div>

        {/* Next page button */}
        <button
          className={`pagination-button ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}
          onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/**
 * EnhancedTableBase component with fixed headers and synchronized horizontal scrolling
 * This is the base component that should be used for all enhanced tables in the application
 */
function EnhancedTableBase<T extends Record<string, any>>({
  columns,
  data,
  keyField = 'id',
  defaultSort,
  onSort,
  maxHeight = 'calc(100vh - 20rem)',
  width = '100%',
  className = '',
  stickyHeader = true,
  horizontalScroll = true,
  loading = false,
  emptyMessage = 'No data available',
  rowClassName,
  onRowClick,
  alternateRowColors = false,
  pagination,
  permissions = {}
}: EnhancedTableProps<T>) {
  // Use the raw data directly without filtering for uniqueness
  // This ensures all records from Supabase are displayed, including any duplicates
  // which is important for admin users to see and clean the data
  const rawData = useMemo(() => {
    if (!data || data.length === 0) return data;
    return data;
  }, [data]);

  // Debug logging for data
  console.log('EnhancedTableBase received data:', {
    dataLength: data.length,
    keyFieldCount: data.length > 0 ? new Set(data.map(item => item[keyField])).size : 0,
    firstFewRecords: data.slice(0, Math.min(5, data.length)).map(item => {
      return { id: item[keyField] };
    }),
    pagination: pagination ? {
      currentPage: pagination.currentPage,
      pageSize: pagination.pageSize,
      totalCount: pagination.totalCount
    } : 'none'
  });
  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const bodyContainerRef = useRef<HTMLDivElement>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(defaultSort || null);

  const topScrollbarRef = useRef<HTMLDivElement>(null);

  // Synchronize horizontal scrolling between header, body, and top scrollbar
  useEffect(() => {
    const headerWrapper = headerWrapperRef.current;
    const bodyContainer = bodyContainerRef.current;
    const topScrollbar = topScrollbarRef.current;

    if (!headerWrapper || !bodyContainer || !topScrollbar) return;

    const handleBodyScroll = () => {
      // Sync header and top scrollbar with body
      headerWrapper.scrollLeft = bodyContainer.scrollLeft;
      topScrollbar.scrollLeft = bodyContainer.scrollLeft;
    };

    const handleTopScroll = () => {
      // Sync body and header with top scrollbar
      bodyContainer.scrollLeft = topScrollbar.scrollLeft;
      headerWrapper.scrollLeft = topScrollbar.scrollLeft;
    };

    // Initial check
    handleBodyScroll();

    bodyContainer.addEventListener('scroll', handleBodyScroll);
    topScrollbar.addEventListener('scroll', handleTopScroll);

    return () => {
      bodyContainer.removeEventListener('scroll', handleBodyScroll);
      topScrollbar.removeEventListener('scroll', handleTopScroll);
    };
  }, [data, columns]);

  // Handle column sorting
  const handleSort = (columnId: string) => {
    if (!onSort) return;

    const newDirection: 'asc' | 'desc' =
      sortConfig?.field === columnId && sortConfig.direction === 'asc'
        ? 'desc'
        : 'asc';

    const newSortConfig: SortConfig = { field: columnId, direction: newDirection };
    setSortConfig(newSortConfig);
    onSort(newSortConfig);
  };

  // Generate column styles
  const columnStyles = useMemo(() => {
    return columns.reduce<Record<string, React.CSSProperties>>((styles, column) => {
      styles[column.id] = {
        width: getColumnWidth(column.width),
        minWidth: column.width === 'flexible' ? 'auto' : getColumnWidth(column.width),
        ...column.width === 'flexible' ? { flex: 1 } : {}
      };
      return styles;
    }, {});
  }, [columns]);

  // Get row key
  const getRowKey = (row: T, index: number) => {
    return row[keyField]?.toString() || `row-${index}`;
  };

  // Get row class name
  const getRowClassName = (row: T, index: number) => {
    let baseClass = 'hover:bg-gray-50';

    // Add alternating row colors if enabled
    if (alternateRowColors && index % 2 === 1) {
      baseClass += ' bg-gray-50';
    }

    if (onRowClick) {
      baseClass += ' clickable';
    }

    if (typeof rowClassName === 'function') {
      return `${baseClass} ${rowClassName(row)}`;
    }

    if (rowClassName) {
      return `${baseClass} ${rowClassName}`;
    }

    return baseClass;
  };

  // Filter columns based on permissions
  const visibleColumns = useMemo(() => {
    return columns.filter(column => {
      // If column has a visibleWhen function, use it to determine visibility
      if (column.visibleWhen) {
        return column.visibleWhen(permissions);
      }
      // Otherwise, column is always visible
      return true;
    });
  }, [columns, permissions]);

  return (
    <div
      className={`enhanced-table-container ${className}`}
      style={{ width: '100%' }}
    >
      {/* Pagination controls at the top */}
      {pagination && (
        <PaginationControls
          pagination={pagination}
          className="px-4 py-2 border-b border-gray-200 mb-2"
        />
      )}

      {/* Table container with direct scrolling */}
      <div className="table-with-scrollbar-container">
        {/* Direct table scrollbar */}
        <div className="table-scrollbar-container">
          <div
            ref={topScrollbarRef}
            className="table-horizontal-scrollbar"
            onScroll={(e) => {
              if (bodyContainerRef.current) {
                bodyContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
              }
              if (headerWrapperRef.current) {
                headerWrapperRef.current.scrollLeft = e.currentTarget.scrollLeft;
              }
            }}
          >
            <div style={{ width, height: '1px' }}></div>
          </div>
        </div>

        {/* Header container */}
        {stickyHeader && (
          <div className="enhanced-table-header-container">
            <div
              ref={headerWrapperRef}
              className="enhanced-table-header-wrapper"
            >
              <table className="enhanced-table" style={{ width }}>
                <thead>
                  <tr>
                    {visibleColumns.map(column => (
                      <th
                        key={column.id}
                        className={`${getCellClassName(column, true)} ${column.sortable ? 'sortable' : ''}`}
                        style={columnStyles[column.id]}
                        onClick={column.sortable ? () => handleSort(column.id) : undefined}
                        data-sort={column.sortable && sortConfig ? getSortAttribute(column.id, sortConfig.field, sortConfig.direction) : undefined}
                        title={typeof column.header === 'string' ? column.header : undefined}
                      >
                        {column.header}
                        {column.sortable && sortConfig && getSortIndicator(column.id, sortConfig.field, sortConfig.direction)}
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
          </div>
        )}

        {/* Body container */}
        <div
          ref={bodyContainerRef}
          className="enhanced-table-body-container enhanced-table-force-scroll"
          style={{
            maxHeight,
            overflowX: horizontalScroll ? 'auto' : 'hidden',
            overflowY: 'auto'
          }}
          onScroll={(e) => {
            if (topScrollbarRef.current) {
              topScrollbarRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
            if (headerWrapperRef.current) {
              headerWrapperRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          {/* Left scroll indicator */}
          {horizontalScroll && <div className="enhanced-table-scroll-left-indicator" />}

          {/* Right scroll indicator */}
          {horizontalScroll && <div className="enhanced-table-scroll-right-indicator" />}

          {loading ? (
            <div className="enhanced-table-loading">
              <div className="enhanced-table-loading-spinner" />
            </div>
          ) : rawData.length === 0 ? (
            <div className="enhanced-table-empty">
              {emptyMessage}
            </div>
          ) : (
            <table className="enhanced-table" style={{ width }}>
              {!stickyHeader && (
                <thead>
                  <tr>
                    {visibleColumns.map(column => (
                      <th
                        key={column.id}
                        className={`${getCellClassName(column, true)} ${column.sortable ? 'sortable' : ''}`}
                        style={columnStyles[column.id]}
                        data-sort={column.sortable && sortConfig ? getSortAttribute(column.id, sortConfig.field, sortConfig.direction) : undefined}
                        title={typeof column.header === 'string' ? column.header : undefined}
                      >
                        {column.header}
                        {column.sortable && sortConfig && getSortIndicator(column.id, sortConfig.field, sortConfig.direction)}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {rawData.map((row: T, rowIndex: number) => (
                  <tr
                    key={getRowKey(row, rowIndex)}
                    className={getRowClassName(row, rowIndex)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {visibleColumns.map(column => (
                      <td
                        key={`${getRowKey(row, rowIndex)}-${column.id}`}
                        className={getCellClassName(column)}
                        style={columnStyles[column.id]}
                        title={getCellTitleProperty(row, column)}
                      >
                        {getCellContent(row, column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Horizontal scroll indicator */}
          {horizontalScroll && (
            <div className="enhanced-table-scroll-indicator" />
          )}
        </div>
      </div>

      {/* Pagination controls at the bottom */}
      {pagination && (
        <PaginationControls
          pagination={pagination}
          className="px-4 py-2 border-t border-gray-200"
        />
      )}
    </div>
  );
}

export default EnhancedTableBase;
